import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MotorVehicleVersionLicensePlate } from '@app/core/interfaces/motor-vehicle';
import { InternationalInsuranceVehicleFormatEnum, InternationalInsuranceVehicleTypeEnum } from '@app/my-zone/enums';
import {
    ErrorInterface,
    InternationalMotorInsuranceCardActionService,
    PdfInternationalMotorInsuranceCardInterface,
    PolicyProductIdEnum,
    PolicyValidityStatusCodeEnum,
    PurchasedProductInterface
} from 'outshared-lib';
import { Subscription } from 'rxjs';
import { InsuranceIMICService } from '../insurance-imic.service';

// Consider splitting caravan and trailer into
// two seperate components for more clarity.
@Component({
    selector: 'ins-insurance-imic-vehicle',
    templateUrl: './insurance-imic-vehicle.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceIMICVehicleComponent implements OnInit, OnDestroy {
    @Input() public vehicleType: InternationalInsuranceVehicleTypeEnum = InternationalInsuranceVehicleTypeEnum.Caravan;
    @Input() public showTitle: boolean = true;
    @Input() public showContent: boolean = true;
    @Input() public purchasedProduct: PurchasedProductInterface;

    @Output() readonly completed: EventEmitter<void> = new EventEmitter();

    public loading: boolean = false;
    public form: UntypedFormGroup;
    public showOptions: boolean = false;
    public errors: string[] = [];
    public downloadSuccess: boolean = false;
    public emailSuccess: boolean = false;
    public productIdEnum: typeof PolicyProductIdEnum = PolicyProductIdEnum;
    public internationalInsuranceVehicleTypeEnum: typeof InternationalInsuranceVehicleTypeEnum = InternationalInsuranceVehicleTypeEnum;
    public internationalInsuranceVehicleFormatEnum: typeof InternationalInsuranceVehicleFormatEnum = InternationalInsuranceVehicleFormatEnum;
    public isDisabled: boolean = false;
    public isCaravanInsured: boolean = false;

    private subscriptions = new Subscription();

    constructor(
        private insuranceIMICService: InsuranceIMICService,
        private internationalMotorInsuranceCardActionService: InternationalMotorInsuranceCardActionService,
        private changeDetectorRef: ChangeDetectorRef
    ) { }

    public get licensePlate(): AbstractControl {
        return this.form.get('licensePlate');
    }

    public get vehicleBrand(): AbstractControl {
        return this.form.get('vehicleBrand');
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public ngOnInit(): void {
        this.isCaravanInsured = this.purchasedProduct?.productId === PolicyProductIdEnum.Caravan;
        this.createForm();
        this.isDisabled = this.setDisabled();

        if (this.isCaravanInsured) {
            this.showOptions = true;
        }
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        const isCaravan = this.vehicleType === InternationalInsuranceVehicleTypeEnum.Caravan;

        switch (this.form.value.format) {
            case InternationalInsuranceVehicleFormatEnum.Pdf: {
                isCaravan && this.isCaravanInsured ? this.submitPdf() : this.submitPdfNotInsured();
                return;
            }
            case InternationalInsuranceVehicleFormatEnum.Email: {
                isCaravan && this.isCaravanInsured ? this.submitEmail() : this.submitEmailNotInsured();
                return;
            }
        }
    }

    public onLicensePlateValueChanged(): void {
        if (!this.licensePlate.value.length) {
            return;
        }

        const isCaravan = this.vehicleType === InternationalInsuranceVehicleTypeEnum.Caravan;

        if (isCaravan) {
            this.getCaravanLicencePlate(this.licensePlate.value);
        } else {
            this.showOptions = true;
        }
    }

    private setDisabled(): boolean {
        return this.purchasedProduct?.product?.validityStatusCode === PolicyValidityStatusCodeEnum.Transfer;
    }

    private submitPdf(): void {
        const productSequenceNumber = this.purchasedProduct?.productSequenceNumber;
        const subscription = this.internationalMotorInsuranceCardActionService
            .loadPdfInternationalMotorInsuranceCard$(productSequenceNumber)
            .subscribe({
                next: (response) => this.onSubmitPdfSuccess(response),
                error: (errors) => this.onError(errors),
            });

        this.startLoading();
        this.subscriptions.add(subscription);
    }

    private submitPdfNotInsured(): void {
        const productSequenceNumber = this.purchasedProduct?.productSequenceNumber;
        const licensePlate = this.insuranceIMICService.formatLicencePlate(this.licensePlate.value);
        const vehicleBrand = this.vehicleBrand.value;
        const subscription = this.internationalMotorInsuranceCardActionService
            .loadPdfNotInsuredInternationalMotorInsuranceCard$(productSequenceNumber, licensePlate, vehicleBrand)
            .subscribe({
                next: (response) => this.onSubmitPdfSuccess(response),
                error: (errors) => this.onError(errors),
            });

        this.startLoading();
        this.subscriptions.add(subscription);
    }

    private submitEmail(): void {
        const productSequenceNumber = this.purchasedProduct?.productSequenceNumber;
        const subscriptionRef = this.internationalMotorInsuranceCardActionService
            .loadEmailInternationalMotorInsuranceCard$(productSequenceNumber)
            .subscribe({
                next: () => this.onSubmitEmailSuccess(),
                error: (errors) => this.onError(errors),
            });

        this.startLoading();
        this.subscriptions.add(subscriptionRef);
    }

    private submitEmailNotInsured(): void {
        const productSequenceNumber = this.purchasedProduct?.productSequenceNumber;
        const licensePlate = this.insuranceIMICService.formatLicencePlate(this.licensePlate.value);
        const vehicleBrand = this.vehicleBrand.value;
        const subscriptionRef = this.internationalMotorInsuranceCardActionService
            .loadEmailNotInsuredInternationalMotorInsuranceCard$(productSequenceNumber, licensePlate, vehicleBrand)
            .subscribe({
                next: () => this.onSubmitEmailSuccess(),
                error: (errors) => this.onError(errors),
            });

        this.startLoading();
        this.subscriptions.add(subscriptionRef);
    }

    private startLoading(): void {
        this.errors = [];
        this.downloadSuccess = false;
        this.emailSuccess = false;
        this.loading = true;
    }

    private createForm(): void {
        if (this.isCaravanInsured) {
            this.form = new UntypedFormGroup({
                format: new FormControl(
                    {
                        value: InternationalInsuranceVehicleFormatEnum.Pdf,
                        disabled: this.setDisabled(),
                    },
                    [Validators.required]
                ),
            });

            return;
        }

        this.form = new UntypedFormGroup({
            licensePlate: new FormControl({ value: '', disabled: this.setDisabled() }, { validators: [Validators.required], updateOn: 'blur' }),
            vehicleBrand: new FormControl({ value: '', disabled: this.setDisabled() }, [Validators.required]),
            format: new FormControl(
                {
                    value: InternationalInsuranceVehicleFormatEnum.Pdf,
                    disabled: this.setDisabled(),
                },
                [Validators.required]
            ),
        });

        this.subscriptions.add(this.licensePlate.valueChanges.subscribe(() => this.onLicensePlateValueChanged()));
    }

    private getCaravanLicencePlate(licencePlate: string): void {
        const subscription = this.insuranceIMICService.getCaravanLicencePlate$(licencePlate).subscribe({
            next: (response) => this.getCaravanLicencePlateSuccess(response),
            error: () => this.getCaravanLicensePlateError(),
        });

        this.startLoading();
        this.resetBrand();
        this.subscriptions.add(subscription);
    }

    private getCaravanLicencePlateSuccess(response: MotorVehicleVersionLicensePlate): void {
        const vehicleBrand = response?.merk ?? ''; // Endpoint needs to be moved to data library

        this.loading = false;
        this.showOptions = true;
        this.vehicleBrand.setValue(vehicleBrand);
        this.changeDetectorRef.detectChanges();
    }

    private resetBrand(): void {
        this.showOptions = false;
        this.vehicleBrand.setValue('');
        this.errors = [];
    }

    private onSubmitEmailSuccess(): void {
        this.loading = false;
        this.emailSuccess = true;
        this.errors = [];
        this.changeDetectorRef.detectChanges();
    }

    private onSubmitPdfSuccess(response: PdfInternationalMotorInsuranceCardInterface): void {
        this.loading = false;
        this.downloadSuccess = true;
        this.errors = [];

        this.insuranceIMICService.downloadFile(
            `data:${response.type};base64,${response.pdf}`,
            response.identifyingName
        );

        this.changeDetectorRef.detectChanges();
    }

    private onError(errors: ErrorInterface[]): void {
        this.errors = errors.map((error) => error.message);
        this.loading = false;
        this.changeDetectorRef.detectChanges();
    }

    private getCaravanLicensePlateError(): void {
        this.errors = [];
        this.loading = false;
        // If the endpoint fails the user still needs to be able to fillin the form.
        this.showOptions = true;
        this.changeDetectorRef.detectChanges();
    }
}
